/**
 * useClearMatFilters - URL-synced filter state for ClearMat directory
 * Enables shareable links with filter parameters
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Distance slider steps (in miles) - aligned with UI labels
export const DISTANCE_STEPS = [10, 25, 50, 100, Infinity];

export const getDistanceLabel = (value: number): string => {
  if (value === 0 || value === Infinity) return 'Any';
  return `${value} mi`;
};

export interface ClearMatFilters {
  query: string;
  distance: number;
  verified: boolean;
  tags: string[];
  affiliation: string;
}

export interface UseClearMatFiltersReturn {
  filters: ClearMatFilters;
  setQuery: (query: string) => void;
  setDistance: (distance: number) => void;
  setVerified: (verified: boolean) => void;
  setTags: (tags: string[]) => void;
  toggleTag: (tag: string) => void;
  setAffiliation: (affiliation: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

const DEFAULT_FILTERS: ClearMatFilters = {
  query: '',
  distance: Infinity,
  verified: false,
  tags: [],
  affiliation: '',
};

/**
 * Hook for managing ClearMat filter state with URL synchronization
 */
export function useClearMatFilters(): UseClearMatFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL on mount
  const initialFilters = useMemo((): ClearMatFilters => {
    const q = searchParams.get('q') || '';
    const distanceStr = searchParams.get('distance');
    const verifiedStr = searchParams.get('verified');
    const tagsStr = searchParams.get('tags');
    const affiliationStr = searchParams.get('affiliation') || '';

    let distance = Infinity;
    if (distanceStr) {
      const parsed = parseInt(distanceStr, 10);
      if (!isNaN(parsed) && DISTANCE_STEPS.includes(parsed)) {
        distance = parsed;
      }
    }

    return {
      query: q,
      distance,
      verified: verifiedStr === 'true',
      tags: tagsStr ? tagsStr.split(',').filter(Boolean) : [],
      affiliation: affiliationStr,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only compute once on mount (intentionally not re-computing on searchParams changes)

  const [filters, setFilters] = useState<ClearMatFilters>(initialFilters);

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.query) {
      params.set('q', filters.query);
    }
    if (filters.distance !== Infinity && filters.distance > 0) {
      params.set('distance', filters.distance.toString());
    }
    if (filters.verified) {
      params.set('verified', 'true');
    }
    if (filters.tags.length > 0) {
      params.set('tags', filters.tags.join(','));
    }
    if (filters.affiliation) {
      params.set('affiliation', filters.affiliation);
    }

    // Only update if params changed
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, setSearchParams, searchParams]);

  const setQuery = useCallback((query: string) => {
    setFilters(prev => ({ ...prev, query }));
  }, []);

  const setDistance = useCallback((distance: number) => {
    setFilters(prev => ({ ...prev, distance }));
  }, []);

  const setVerified = useCallback((verified: boolean) => {
    setFilters(prev => ({ ...prev, verified }));
  }, []);

  const setTags = useCallback((tags: string[]) => {
    setFilters(prev => ({ ...prev, tags }));
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag],
    }));
  }, []);

  const setAffiliation = useCallback((affiliation: string) => {
    setFilters(prev => ({ ...prev, affiliation }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.query !== '' ||
      (filters.distance !== Infinity && filters.distance > 0) ||
      filters.verified ||
      filters.tags.length > 0 ||
      filters.affiliation !== ''
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.query) count++;
    if (filters.distance !== Infinity && filters.distance > 0) count++;
    if (filters.verified) count++;
    count += filters.tags.length;
    if (filters.affiliation) count++;
    return count;
  }, [filters]);

  return {
    filters,
    setQuery,
    setDistance,
    setVerified,
    setTags,
    toggleTag,
    setAffiliation,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}

// Re-export useDebounce from its canonical location
export { useDebounce } from './useDebounce';

export default useClearMatFilters;
